import { useState, useRef, useEffect, useCallback } from 'react'
import useDealStore from '../store/dealStore'
import '../styles/pipelineChatbot.css'

// ── Groq API config ───────────────────────────────────────────────────────────
// Get a FREE key at: https://console.groq.com → API Keys
const GROQ_API_KEY = import.meta.env.VITE_GROQ_API_KEY || ''
const GROQ_URL     = 'https://api.groq.com/openai/v1/chat/completions'
const GROQ_MODEL   = 'llama-3.3-70b-versatile'

const MODULE_NAMES = {
  'quick-deal': 'Quick Deal Analysis',
  1:  'Deal Lead Generation',
  2:  'Market Intelligence',
  3:  'Deal Intake & Documents',
  4:  'Underwriting Engine',
  5:  'Deal Score & Verdict',
  6:  'Lender Match Engine',
  7:  'Deal Syndication Engine',
  8:  'Loan Package Generator',
  9:  'Outreach & Communication',
  10: 'Actions & Follow-up',
  11: 'LOI Generator',
  12: 'PSA Generator',
  13: 'Escrow & Title Workflow',
  14: 'Due Diligence & Title Clearance',
  15: 'Financing, Closing & Recording',
  16: 'Asset Plan & Hold',
  17: 'Exit Strategy',
}

const QUICK_PROMPTS = [
  'What is my current deal score?',
  'Explain my cap rate',
  'What should I do next?',
  'How is DSCR calculated?',
  'Is this a good deal?',
  'What does the lender match mean?',
]

// ── Build system prompt from deal state ───────────────────────────────────────
function buildSystemPrompt(dealState, activeModule) {
  const raw  = dealState.rawDealData || {}
  const uw   = dealState.underwritingMetrics || {}
  const score = dealState.scoreAndOffer || {}
  const lm   = dealState.lenderMatches || {}
  const fin  = dealState.extractedFinancials || {}

  const dealInfo = dealState.dealId ? `
Current Deal:
- Property: ${raw.propertyName || raw.name || 'Unknown'}
- Address: ${raw.location?.address || '-'}, ${raw.location?.city || '-'}, ${raw.location?.state || '-'}
- Price: $${Number(raw.price || 0).toLocaleString()}
- Units: ${raw.property?.units || '-'}
- NOI: $${Number(fin.noi || uw.noi || 0).toLocaleString()}
- Cap Rate: ${uw.capRate?.entry ? (uw.capRate.entry).toFixed(2) + '%' : '-'}
- DSCR: ${uw.dscr?.current || '-'}
- LTV: ${uw.debtService?.ltv ? (uw.debtService.ltv * 100).toFixed(1) + '%' : '-'}
- Loan Amount: $${Number(uw.debtService?.loanAmount || 0).toLocaleString()}
- Interest Rate: ${uw.debtService?.interestRate || '-'}%
- Deal Score: ${score.numericalScore || '-'} / 100
- Verdict: ${score.verdict || '-'}
- Recommended Offer: $${Number(score.offerPrice || 0).toLocaleString()}
- Pipeline Stage: ${dealState.pipelineStage || 1}
- Active Module: ${MODULE_NAMES[activeModule] || activeModule}
- Top Lender: ${lm.topRecommendation?.lenderName || 'Not selected yet'}
- Lender Approved: ${lm.topRecommendation?.userApproved ? 'Yes' : 'No'}
` : 'No deal selected yet.'

  return `You are AcquireOS AI Assistant — an expert real estate acquisition advisor embedded inside AcquireOS, a professional real estate deal management platform.

${dealInfo}

Pipeline Modules (in order):
1. Deal Lead Generation — find and score deals
2. Market Intelligence — analyze the local market
3. Deal Intake & Documents — upload OM, rent roll, financials
4. Underwriting Engine — run full financial underwriting
5. Deal Score & Verdict — AI scores the deal 0-100 with offer recommendation
6. Lender Match Engine — match the deal to the best lenders
7. Deal Syndication Engine — optional: syndicate with co-investors
8. Loan Package Generator — generate lender submission package
9. Outreach & Communication — email templates to lenders and brokers
10. Actions & Follow-up — task list and timeline
11. LOI Generator — draft Letter of Intent
12. PSA Generator — draft Purchase & Sale Agreement
13. Escrow & Title Workflow — open escrow, set timeline
14. Due Diligence & Title Clearance — DD checklist and title review
15. Financing, Closing & Recording — closing costs and wire authorization
16. Asset Plan & Hold — KPIs, property management, hold strategy
17. Exit Strategy — model sale, refinance, or hold scenarios

Your job:
- Answer questions about the current deal's financials, pipeline status, and next steps
- Explain underwriting metrics (cap rate, DSCR, LTV, NOI, IRR, CoC) clearly
- Guide the user on what to do in each module
- Be concise, direct, and data-driven
- If no deal is loaded, guide them to start with Quick Deal Analysis
- Always use the actual deal numbers in your answers when available
- Keep responses under 150 words unless the user asks for detail`
}

// ── Call Groq API (OpenAI-compatible) ─────────────────────────────────────────
async function callGroq(messages, systemPrompt) {
  if (!GROQ_API_KEY) {
    return '⚠️ No Groq API key found.\n\n1. Go to https://console.groq.com\n2. Sign up free → API Keys → Create key\n3. Add VITE_GROQ_API_KEY=your_key to your .env file\n4. Restart the app'
  }

  const res = await fetch(GROQ_URL, {
    method: 'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${GROQ_API_KEY}`,
    },
    body: JSON.stringify({
      model:       GROQ_MODEL,
      temperature: 0.7,
      max_tokens:  512,
      messages: [
        { role: 'system', content: systemPrompt },
        ...messages.map(m => ({ role: m.role === 'assistant' ? 'assistant' : 'user', content: m.content })),
      ],
    }),
  })

  const data = await res.json()
  if (!res.ok) throw new Error(data?.error?.message || `Groq API error ${res.status}`)
  return data.choices?.[0]?.message?.content || 'No response received.'
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function PipelineChatbot() {
  const { dealState, activeModule } = useDealStore()

  const [open,           setOpen]           = useState(false)
  const [messages,       setMessages]       = useState([
    { role: 'assistant', content: '👋 Hi! I\'m your AcquireOS AI Assistant. Ask me anything about your deal, pipeline, or underwriting metrics.' }
  ])
  const [input,          setInput]          = useState('')
  const [loading,        setLoading]        = useState(false)
  const [error,          setError]          = useState('')
  const [showGreeting,   setShowGreeting]   = useState(false)
  const [greetingHiding, setGreetingHiding] = useState(false)
  const [fabPhase,       setFabPhase]       = useState('icon') // 'icon' | 'hi'

  const bottomRef = useRef(null)
  const inputRef  = useRef(null)

  // Cycle between the icon and a short Hi greeting when chat is closed.
  useEffect(() => {
    if (open) { setFabPhase('icon'); return }
    const sequence  = ['icon', 'hi']
    const durations = [1400,   1100]
    let idx = 0
    let timerId
    setFabPhase('icon')
    const run = () => {
      timerId = setTimeout(() => {
        idx = (idx + 1) % sequence.length
        setFabPhase(sequence[idx])
        run()
      }, durations[idx])
    }
    run()
    return () => clearTimeout(timerId)
  }, [open])

  // Show greeting bubble after 2.5 s
  useEffect(() => {
    const t = setTimeout(() => setShowGreeting(true), 2500)
    return () => clearTimeout(t)
  }, [])

  // Auto-dismiss greeting after 8 s of visibility
  useEffect(() => {
    if (!showGreeting) return
    const t = setTimeout(() => dismissGreeting(), 8000)
    return () => clearTimeout(t)
  }, [showGreeting])

  const dismissGreeting = useCallback(() => {
    setGreetingHiding(true)
    setTimeout(() => { setShowGreeting(false); setGreetingHiding(false) }, 320)
  }, [])

  const handleOpen = useCallback(() => {
    if (showGreeting) dismissGreeting()
    setOpen(o => !o)
  }, [showGreeting, dismissGreeting])

  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
      inputRef.current?.focus()
    }
  }, [messages, open])

  const sendMessage = useCallback(async (text) => {
    const userText = (text || input).trim()
    if (!userText || loading) return
    setInput('')
    setError('')

    const newMessages = [...messages, { role: 'user', content: userText }]
    setMessages(newMessages)
    setLoading(true)

    try {
      const systemPrompt = buildSystemPrompt(dealState, activeModule)
      const reply = await callGroq(newMessages, systemPrompt)
      setMessages(prev => [...prev, { role: 'assistant', content: reply }])
    } catch (err) {
      setError(err.message || 'Something went wrong. Try again.')
    } finally {
      setLoading(false)
    }
  }, [input, loading, messages, dealState, activeModule])

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const handleClear = () => {
    setMessages([{ role: 'assistant', content: '👋 Hi! I\'m your AcquireOS AI Assistant. Ask me anything about your deal, pipeline, or underwriting metrics.' }])
    setError('')
  }

  return (
    <>
      {/* Greeting bubble — appears to the left of the FAB */}
      {showGreeting && !open && (
        <div className={`pcb-greeting ${greetingHiding ? 'pcb-greeting-hide' : ''}`}>
          <span>👋 Hi! Need help with your deal?</span>
          <button className="pcb-greeting-close" onClick={dismissGreeting} aria-label="Dismiss">✕</button>
        </div>
      )}

      {/* Floating Button */}
      <button
        className={`pcb-fab ${open ? 'pcb-fab-open' : 'pcb-fab-bump'} ${fabPhase === 'hi' && !open ? 'pcb-fab-hi' : ''} ${loading ? 'pcb-fab-loading' : ''}`}
        onClick={handleOpen}
        title="AcquireOS AI Chat Assistant — Ask anything about your deal"
        aria-label="Open AI Chat Assistant"
      >
        {open ? (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M5 5L15 15M15 5L5 15" stroke="white" strokeWidth="2.2" strokeLinecap="round"/>
          </svg>
        ) : fabPhase === 'hi' ? (
          <span key="hi" className="pcb-fab-text">
            <span className="pcb-fab-emoji">👋</span>
            <span className="pcb-fab-word">Hi!</span>
          </span>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M12 2C6.48 2 2 6.48 2 12c0 1.85.5 3.58 1.37 5.06L2 22l4.94-1.37A9.96 9.96 0 0 0 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2z" fill="white" opacity=".9"/>
            <circle cx="8.5"  cy="12" r="1.2" fill="#1d4ed8"/>
            <circle cx="12"   cy="12" r="1.2" fill="#1d4ed8"/>
            <circle cx="15.5" cy="12" r="1.2" fill="#1d4ed8"/>
          </svg>
        )}
        {loading && <span className="pcb-fab-pulse" />}
      </button>

      {/* Chat Panel */}
      {open && (
        <div className="pcb-panel">
          {/* Header */}
          <div className="pcb-header">
            <div className="pcb-header-info">
              <div className="pcb-header-dot" />
              <div>
                <div className="pcb-header-title">AcquireOS AI Assistant</div>
                <div className="pcb-header-sub">
                  {MODULE_NAMES[activeModule] ? `📍 ${MODULE_NAMES[activeModule]}` : 'Powered by Groq'}
                </div>
              </div>
            </div>
            <button className="pcb-clear-btn" onClick={handleClear} title="Clear chat">↺</button>
          </div>

          {/* Messages */}
          <div className="pcb-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`pcb-msg ${msg.role === 'user' ? 'pcb-msg-user' : 'pcb-msg-bot'}`}>
                {msg.role === 'assistant' && <div className="pcb-bot-avatar">AI</div>}
                <div className="pcb-msg-bubble">
                  {msg.content.split('\n').map((line, j) => (
                    <span key={j}>{line}{j < msg.content.split('\n').length - 1 && <br />}</span>
                  ))}
                </div>
              </div>
            ))}

            {loading && (
              <div className="pcb-msg pcb-msg-bot">
                <div className="pcb-bot-avatar">AI</div>
                <div className="pcb-msg-bubble pcb-typing">
                  <span /><span /><span />
                </div>
              </div>
            )}

            {error && (
              <div className="pcb-error">{error}</div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Quick Prompts */}
          {messages.length <= 1 && (
            <div className="pcb-quick-prompts">
              {QUICK_PROMPTS.map((p, i) => (
                <button key={i} className="pcb-quick-btn" onClick={() => sendMessage(p)}>
                  {p}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="pcb-input-row">
            <textarea
              ref={inputRef}
              className="pcb-input"
              rows={1}
              placeholder="Ask anything about your deal..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
            <button
              className="pcb-send-btn"
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  )
}
